import React from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@mui/styles';

import { Button, Table, TableBody } from '@mui/material';

import Node from '../../common/Node';
import Loader from '../../common/Loader';
import Search from '../../common/Search';
import CommonStyles from '../../common/CommonStyles';
import ComponentWrapper from '../../common/ComponentWrapper';
import FullScreenDialog from '../../common/FullScreenDialog';

import BoundsList from './components/BoundsList';
import Restriction from './components/Restriction';
import RestrictionsAcc from './components/RestrictionsAcc';
import DeletedRestriction from './components/DeletedRestriction';
import AbstractRestrictionItem from './components/AbstractRestrictionItem';

import {
  changeArrayElementPosition,
  editRstMacroArgs,
  getUrlParameter,
} from '../../utils';

import ActionConfirmation from '../../common/ActionConfirmation';

import produce from "immer"
import { cloneDeep } from '../../utils/lodash';

const styles = {
  mainContainer: {
    width: '1150px',
    padding: '20px',
    margin: '0px auto',
  },
  deletedRst: {
    display: 'flex',
    color: '#4E4C4C',
    padding: '10px 0',
    background: '#fff',
    marginBottom: '5px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  conceptAccHead: {
    color: '#fff',
    fontSize: '24px',
    padding: '10px 33px',
    background: '#FDFDFD',
    textDecoration: 'underline',
    marginBottom: '20px',
    '&:hover': {
      color: '#fff',
      cursor: 'pointer',
      background: '#fff',
      textDecoration: 'none',
    },
  },
  active: {
    color: '#fff',
    cursor: 'pointer',
    textDecoration: 'none',
    marginBottom: '5px',
  },
  ...CommonStyles,
};

const emptyRst = {
  id: 0,
  oid: 0,
  type: '',
  func: '',
  value: '',
  regex: '',
  data: `{}`,
  render: '',
  isa: false,
  timezones: [],
  isEdited: false,
  isOpened: false,
  boundaries: '',
  inverted: false,
  unique_sid: null,
  subjectMaxBound: 1,
  subjectMinBound: 0,
  isPredicate: false,
  isSingleton: false,
  value_expression: '',
  r_subjectMaxBound: 0,
  r_subjectMinBound: 0,
  implemented_pids: [],
  is_bounds_func: false,
};

const TYPES_WITHOUT_FORMATTERS = ['BUTTON', 'IMAGE', 'FILE', 'GEOLOCATION', 'MW_LOCATION', 'TELEGRAM_LOCATION'];
export const TYPES_WITHOUT_BOUNDS = [...TYPES_WITHOUT_FORMATTERS, 'BOOLEAN', 'INSTOCK'];

class RestrictionsPage extends ComponentWrapper {
  state = {
    concepts: [],
    loaded: false,
    ancestor: null,
    deleteModal: {
      rstId: 0,
      concept: 0,
      rstIndex: 0,
      isOpened: false,
      rstTitle: "",
      contentText: "",
    },
    rstTypes: [],
    rstTypeInfo: null,
    formatters: [],
    boundsModal: {
      bounds: [],
      boundsNew: '',
      isOpened: false,
    },
    oldRstCopies: {},
    moveSelectedTo: 0,
    nodes: {},
    rstrs: {},
    abstractRstrs: {},
    interfacesConceptIds: [],
  };

  componentDidMount() {
    document.title=`Restrictions edit | Milkyweb`;

    const rstFromUrl = +window.location.href.split('&')[1];
    const rstIdToFocus = getUrlParameter('pid', rstFromUrl);

    const ancestor = +getUrlParameter('id');

    this.get({
      url: `/get_restr?id=${ancestor}&meta_query=["restrictions_types","predicates_formatters","timezones"]`,
      cb: (fullRstData) => {

        const rstTypeInfo = fullRstData._meta_query.restrictions_types;
        let rstTypes = [];
        if (rstTypeInfo) {
          rstTypes = Object.keys(rstTypeInfo)
            .sort((a, b) => {
              if (a === 'STRING' || a === 'OBJECT') return -1;
              return a.localeCompare(b);
            });
        }

        const formatters = fullRstData._meta_query.predicates_formatters || [];
        for (const key of Object.keys(formatters)) {
          for (const formatter of formatters[key]) formatter.value = formatter.name;
        }

        const timezones = fullRstData._meta_query.timezones || [];

        rstTypes.unshift('Select type');

        const { nodes, rstrs } = fullRstData;

        let tSupersImplicit = nodes[ancestor].box.t_supers_implicit || [];

        const interfacesConceptIds1 = nodes[ancestor].box.i_implicit && nodes[ancestor].box.i_implicit.length ?
          nodes[ancestor].box.i_implicit : []; //collect interface ids +from supers

        const abstractConceptIds = nodes[ancestor].box.t_supers_implicit && nodes[ancestor].box.t_supers_implicit.length ?
          nodes[ancestor].box.t_supers_implicit.filter(concept => nodes[concept].is_abstract) : []; //collect abstract concepts ids +from supers

        const interfacesConceptIds = [...interfacesConceptIds1, abstractConceptIds].flat();

        const abstractRstrs = {};
        for (const cid of interfacesConceptIds) {
          const rstsByCid = cloneDeep(rstrs[cid]);
          if (rstsByCid) {
            const abstractRstsByCid = [];
            for (const rid of Object.keys(rstsByCid)) {
              if ((rstsByCid[rid].isAbstract && abstractConceptIds.includes(cid)) || interfacesConceptIds1.includes(cid)) {
                abstractRstsByCid.push(rstsByCid[rid]);
                delete rstsByCid[rid];
              }
            }

            if (abstractRstsByCid.length) abstractRstrs[cid] = abstractRstsByCid;
          }
        }

        const usedInterfaces = [];
        const concepts  = [];
        const rboxConcepts = [...tSupersImplicit, +ancestor];

        rboxConcepts.forEach(conceptId => {
          const concept = {
            id: conceptId,
            atLeastOneRstOpened: false,
            restrictions: [],
            hasOverriders: false,
          };

          Object.keys(rstrs[conceptId]).forEach((rstId) => {
            const rst = rstrs[conceptId][rstId];

            if (+rstIdToFocus === +rstId ) {
              rst.isEdited = true;
              rst.isOpened = true;
            } else {
              rst.isEdited = false;
              rst.isOpened = false;
            }
            rst.data = JSON.stringify(rst.data);

            if (!rst.implemented_pids) {
              rst.implemented_pids = []
            }

            if (rst.implemented_pids.length) {
              rst.implemented_pids.forEach((rstId) => {
                usedInterfaces.push(rstId);
              })
            }

            if (!rst.value_expression) rst.value_expression = '';

            if (rst.precursor) {
              rst.precursor.overridden_by = rst.overridden_by;
              rst.precursor.data = JSON.stringify(rst.precursor.data);

              concept.hasOverriders = true;

              concept.restrictions.push(rst.precursor);
            } else {
              concept.restrictions.push(rst);
            }

            if (rst.overridder) {
              concept.hasOverriders = true;

              rst.overridder.data = JSON.stringify(rst.overridder.data);

              concept.restrictions.push(rst.overridder);
            }
            if (rst.isEdited) this.saveRstCopy(conceptId, rstId, rst);
          });

          if (conceptId === +ancestor) {
            const newRst = { ...emptyRst };

            newRst.title = 'New rst';
            newRst.sid = conceptId;
            newRst.implemented_pids = [];

            concept.restrictions.push(newRst);
          }

          concepts.push(concept);
        });

        this.setState({
          rstrs,
          nodes,
          ancestor,
          concepts,
          rstTypes,
          timezones,
          formatters,
          rstTypeInfo,
          loaded: true,
          abstractRstrs,
          usedInterfaces,
          interfacesConceptIds,
        }, () => {
          if (rstIdToFocus) {
            const el = document.getElementById(+rstIdToFocus);
            el.scrollIntoView({ block: "center" });
          }
        });
      },
    });
  };

  toggleRstAccordeon = (conceptIndex, rstIndex) => {
    const { concepts } = this.state;

    const concept = concepts[conceptIndex];
    const rstCopy = { ...concept.restrictions[rstIndex] };
    const isOpened = rstCopy.isOpened;

    rstCopy.isOpened = !isOpened;

    concept.restrictions[rstIndex] = rstCopy;
    concept.atLeastOneRstOpened = concept.restrictions.some(rst => rst.isOpened);

    this.setState({ concepts });
  };

  getRestrictionByImplementedPid = (abstrRid) => {
    const { concepts } = this.state;

    for (const concept of concepts) {
      for (let rst of concept.restrictions) {
        if (rst.implemented_pids && rst.implemented_pids.includes(abstrRid)) return rst;
      }
    }
  }

  findUsedInterfaces = (conceptIndex) => {
    const { concepts } = this.state;

    const concept = concepts[conceptIndex];
    let rstrs = concept.restrictions;
    const usedInterfaces = [];

    rstrs.forEach((rst) => {
      if (rst.implemented_pids && rst.implemented_pids.length) {
        rst.implemented_pids.forEach(interfaceId => usedInterfaces.push(interfaceId));
      }
    })

    this.setState({ usedInterfaces });
  };

  getRstInterfaces = (conceptIndex, rstIndex) => { //get interfaces rsts for interface select
    const { interfacesConceptIds, nodes, rstrs, concepts, usedInterfaces, oldRstCopies, abstractRstrs } = this.state;

    const rst = concepts[conceptIndex].restrictions[rstIndex];

    let concept = concepts[conceptIndex];

    let oldRst;
    if (oldRstCopies[concept.id]) {
      oldRst = oldRstCopies[concept.id][rst.id];
    }

    rst.interfaces = Object.values(interfacesConceptIds.map(conceptId => {

      const node = nodes[conceptId];

      const restrictions =
        node.is_interface && rstrs[conceptId] // if ioi
          ? rstrs[conceptId]
          : node.is_abstract && abstractRstrs[conceptId]
            ? Object.values(abstractRstrs[conceptId]).filter(rst => rst.isAbstract)
              : [];

      const interfaceRstsIds = [0];
      const interfaceRstsValues = [
        { title: 'none', value: 'none' },
      ];

      if (restrictions)
      Object.values(restrictions).forEach((item) => {
        if (rst.type === item.type) {
          if (usedInterfaces.length) {
            if ((rst.implemented_pids && rst.implemented_pids.includes(item.id) || !usedInterfaces.includes(item.id))
            || (oldRst && Object.keys(oldRst).includes('implemented_pids') && oldRst.implemented_pids.includes(item.id))) {
              interfaceRstsIds.push(item.id);
              interfaceRstsValues.push({ title: item.id + ' ' + item.title, value: item.id });
            }
          } else {
            interfaceRstsIds.push(item.id);
            interfaceRstsValues.push({ title: item.id + ' ' + item.title, value: item.id });
          }
        }
      });

      const allInterfaces = {
        id: conceptId,
        node,
        interfaceRstsIds,
        interfaceRstsValues,
      };

      return allInterfaces;
    }));
    concepts[conceptIndex].restrictions[rstIndex] = rst;

    this.setState({ concepts });
  };

  toggleConceptRestrictions = (conceptIndex, isOpened) => {
    const { concepts } = this.state;

    const concept = concepts[conceptIndex];

    for (let rstIndex = 0; rstIndex < concept.restrictions.length; rstIndex++) {
      const rst = concept.restrictions[rstIndex];
      if (!rst.id && !rst.isEdited && isOpened) continue;

      const rstCopy = { ...rst };
      rstCopy.isOpened = isOpened;

      concept.restrictions[rstIndex] = rstCopy;
    }

    concept.atLeastOneRstOpened = isOpened;

    this.setState({ concepts: [...concepts] });
  };

  onSaveRstBtnClick = (conceptIndex, rstIndex) => {
    const { concepts, ancestor, interfacesConceptIds } = this.state;

    const isAncestor = concepts[conceptIndex].id === ancestor;
    const rst = concepts[conceptIndex].restrictions[rstIndex];

    if (interfacesConceptIds)
    this.findUsedInterfaces(conceptIndex);

    try {
      rst.data = JSON.parse(rst.data);
    } catch (e) {
      this.notify('error', `Please, enter valid JSON`);

      return;
    }

    const rstCopy = { ...rst };

    rstCopy.isLoading = true;
    rstCopy.data = JSON.stringify(rst.data);
    concepts[conceptIndex].restrictions[rstIndex] = rstCopy;

    this.setState({ concepts }, () => {
      if (rst.id === 'OVR') rst.id = 0;

      if (rst.interfaces) delete rst.interfaces;

      this.postWithData({
        url: `/rest/upd_restr`,
        data: rst,
        cb: (res) => {
          if (res.status === 'error') {
            rst.data = JSON.stringify(rst.data);
            rst.isLoading = false;

            concepts[conceptIndex].restrictions[rstIndex] = rst;

            this.setState({ concepts });

            return;
          }

          const { restr } = res;

          restr.isOpened = true;
          restr.isEdited = false;
          restr.data = JSON.stringify(restr.data);
          restr.implemented_pids = restr.implemented_pids || [];

          if (!rst.id) {
            concepts[conceptIndex].restrictions.splice(rstIndex, 1, restr);

            if (isAncestor) {
              const newRst = { ...emptyRst };

              newRst.title = 'New rst';
              newRst.sid = concepts[conceptIndex].id;

              concepts[conceptIndex].restrictions.push(newRst);
            }
          } else {
            concepts[conceptIndex].restrictions.splice(rstIndex, 1, restr);
          }

          if (interfacesConceptIds)
          this.getRstInterfaces(conceptIndex, rstIndex);

          this.setState({ concepts });
        },
        errorCb: () => {
          rst.isLoading = false;
          rst.data = JSON.stringify(rst.data);

          concepts[conceptIndex].restrictions.splice(rstIndex, 1, rst);

          this.setState({ rst });
        },
      });
    });
  };

  toggleDeleteMsgModal = (conceptIndex, rstIndex, rstId, rstTitle, isOpened, type) => {
    const { deleteModal } = this.state;

    deleteModal.rstId = rstId;
    deleteModal.isOpened = isOpened;
    deleteModal.rstIndex = rstIndex;
    deleteModal.conceptIndex = conceptIndex;
    deleteModal.rstTitle = rstTitle;
    deleteModal.contentText = type;

    this.setState({ deleteModal });
  };

  deleteRestriction = () => {
    const { concepts, deleteModal } = this.state;

    this.postWithData({
      url: `/rest/del_restr`,
      data: { id: deleteModal.rstId },
      cb: () => {
        if (deleteModal.rstId !== 'OVR') {
          concepts[deleteModal.conceptIndex].restrictions.splice(deleteModal.rstIndex, 1, {
            isDeleted: true,
            id: deleteModal.rstId,
          });
        } else {
          concepts[deleteModal.conceptIndex].restrictions.splice(deleteModal.rstIndex, 1);
        }

        this.toggleDeleteMsgModal(0, 0, 0, "", false, "");

        this.setState({ concepts });
      },
    });
  };

  onRstFieldChange = (conceptIndex, rstIndex, field, value) => {
    this.setState((state) => {
      const nextConcepts = produce(state.concepts, mutableConcepts => {
        mutableConcepts[conceptIndex].restrictions[rstIndex][field] = value;
      });

      return {
        concepts: nextConcepts
      }
    });
  };

  onTimezoneDefaultFieldChange = (conceptIndex, rstIndex, value) => {
    this.setState((state) => {
      const nextConcepts = produce(state.concepts, mutableConcepts => {
        mutableConcepts[conceptIndex].restrictions[rstIndex].timezones = mutableConcepts[conceptIndex].restrictions[rstIndex].timezones.map(zone => {
          return { id: zone.id }
        });
        if (value) {
          const timezoneIndex = mutableConcepts[conceptIndex].restrictions[rstIndex]
            .timezones.findIndex(timezone => (value === 'empty' && !timezone.id) || (timezone.id === value));

          
          mutableConcepts[conceptIndex].restrictions[rstIndex].timezones[timezoneIndex].default = true;
        }
      });

      return {
        concepts: nextConcepts
      }
    });
  };

  onMacroFieldChange = (conceptIndex, rstIndex, field, value) => {
    this.setState((state) => {
      const nextConcepts = produce(state.concepts, mutableConcepts => {
        mutableConcepts[conceptIndex].restrictions[rstIndex].macro[field] = value;
      });

      return {
        concepts: nextConcepts
      }
    });
  };

  getDefaultFormatters = (type) => {
    const { formatters, rstTypeInfo } = this.state;

    if (type && rstTypeInfo[type] && rstTypeInfo[type].primitive && !TYPES_WITHOUT_FORMATTERS.includes(type)) {
      const formattersByType = formatters[rstTypeInfo[type].primitive];
      if (formattersByType) {
        const defaultFormatters = formattersByType.filter(f => f.is_default).map(f => f.name);
        if (defaultFormatters.length) return defaultFormatters;
      }
    }
  };

  getSelectFormatters = (type) => {
    const { formatters, rstTypeInfo } = this.state;

    if (!type || !formatters || !rstTypeInfo || TYPES_WITHOUT_FORMATTERS.includes(type)) return;

    return formatters[rstTypeInfo[type].primitive];
  }

  onRstTypeSelect = (conceptIndex, rstIndex, value) => {
    const { concepts, rstTypeInfo } = this.state;

    const rst = { ...concepts[conceptIndex].restrictions[rstIndex] };
    const defaultFormatters = this.getDefaultFormatters(value);

    if (rst.id) {
      rst.type = value;
      const newPrimitive = rstTypeInfo[rst.type].primitive;

      if (rst.formatters && rst.formatters.length && !rst.formattersTemp) {
        rst.formattersTemp = [...rst.formatters];
        rst.primitiveTemp = rst.primitive;
      }

      if (rst.primitiveTemp && rst.primitiveTemp === newPrimitive && rst.formattersTemp) {
        rst.formatters = TYPES_WITHOUT_FORMATTERS.includes(value) ? [] : [...rst.formattersTemp];
      } else {
        rst.formatters = defaultFormatters || [];
      }

      if (rst.bounds && rst.bounds.length && TYPES_WITHOUT_BOUNDS.includes(rst.type)) {
        rst.boundsTemp = [...rst.bounds];
        rst.bounds = [];
      } else if (rst.boundsTemp && !TYPES_WITHOUT_BOUNDS.includes(rst.type)) {
        rst.bounds = [...rst.boundsTemp];
        delete rst.boundsTemp;
      }

      concepts[conceptIndex].restrictions.splice(rstIndex, 1, rst);
    } else {
      const newRst = { ...emptyRst };

      newRst.type = value;
      newRst.title = 'New rst';
      newRst.isEdited = true;
      newRst.isOpened = true;
      newRst.sid = concepts[conceptIndex].id;
      newRst.implemented_pids = [];
      newRst.default_method = 0;

      if (value && value === 'INTEGER') newRst.next_autoincrement_value = 1;

      if (defaultFormatters) newRst.formatters = defaultFormatters;

      concepts[conceptIndex].restrictions.splice(rstIndex, 1, newRst);
    }

    this.setState({ concepts });
  };

  onRstInterfaceSelect = (item, value, conceptIndex, rstIndex) => {
    const { concepts } = this.state;
    const concept = concepts[conceptIndex];
    const restrictions = [...concept.restrictions];
    let rst = { ...restrictions[rstIndex] };
  
    const implementedPidsSet = new Set(rst.implemented_pids);
  
    item.interfaceRstsIds.forEach((rstId) => {
      if (implementedPidsSet.has(rstId)) {
        implementedPidsSet.delete(rstId);
      }
    });
  
    if (value !== 'none' && !implementedPidsSet.has(value)) {
      implementedPidsSet.add(value);
    }
  
    rst = {
      ...rst,
      implemented_pids: [...implementedPidsSet],
    };

    restrictions.splice(rstIndex, 1, rst);
  
    this.setState({
      concepts: concepts.map((c, index) => (index === conceptIndex ? { ...c, restrictions } : c)),
    });
  };

  onOverrideRstClick = (conceptIndex, rst, rstIndex) => {
    const { ancestor, concepts } = this.state;

    const rstCopy = { ...rst };
    const newRst = { ...rst };

    rstCopy.isOpened = false;
    rstCopy.isEdited = false;

    newRst.id = 'OVR';
    newRst.sid = ancestor;
    newRst.isEdited = true;
    newRst.overriding = rstCopy.id;

    concepts[conceptIndex].restrictions[rstIndex] = rstCopy;
    concepts[conceptIndex].restrictions.splice(rstIndex + 1, 0, newRst);

    this.setState({ concepts });
  };

  addNodeToState = (node) => {
    const { nodes } = this.state;

    nodes[node.id] = node;

    this.setState({ nodes });
  };

  onMoveToSearchResultClick = (props) => {
    this.setState({ moveSelectedTo: props.node });
  };

  onMoveToBtnClick = () => {
    const { concepts, ancestor, moveSelectedTo } = this.state;

    const data = {};
    data.pids = [];
    data.cid = ancestor;
    data.move_to = moveSelectedTo.id;

    const selectedRestrictionsIndexes = {};

    concepts.forEach((concept, conceptIndex) => {
      concept.restrictions.forEach((rst) => {
        if (!selectedRestrictionsIndexes[conceptIndex]) selectedRestrictionsIndexes[conceptIndex] = [];

        if (rst.isSelected) {
          data.pids.push(rst.id);
          selectedRestrictionsIndexes[conceptIndex].push(rst.id);
        }
      });
    });

    this.postWithData({
      url: `/rest/restrs_actions`,
      data,
      cb: () => {
        Object.keys(selectedRestrictionsIndexes).forEach((conceptIndex) => {
          const concept = selectedRestrictionsIndexes[conceptIndex];

          concept.forEach((rstId) => {
            const index = concepts[conceptIndex].restrictions.findIndex((rst) => rst.id === rstId);

            concepts[conceptIndex].restrictions.splice(index, 1);
          });
        });

        this.setState({ concepts, moveSelectedTo: {} });
      },
    });
  };

  removeRst = (conceptIndex, rstIndex) => {
    const { concepts } = this.state;

    concepts[conceptIndex].restrictions.splice(rstIndex, 1);

    this.setState({ concepts });
  };

  saveRstCopy = (conceptId, rstId, rst) => {
    const { oldRstCopies } = this.state;

    if (!oldRstCopies[conceptId]) {
      oldRstCopies[conceptId] = {};
    }

    oldRstCopies[conceptId][rstId] = JSON.parse(JSON.stringify(rst));

    this.setState({ oldRstCopies });
  };

  returnOldRst = (conceptId, rstId) => {
    const { concepts, oldRstCopies } = this.state;

    const conceptIndex = concepts.findIndex((fConcept) => fConcept.id === conceptId);

    if (rstId === 0) {
      const newRst = { ...emptyRst };

      newRst.sid = conceptId;
      newRst.title = 'New rst';

      concepts[conceptIndex].restrictions[concepts[conceptIndex].restrictions.length - 1] = newRst;
    } else {
      const rst = oldRstCopies[conceptId][rstId];
      rst.isEdited = false;

      const rstIndex = concepts[conceptIndex].restrictions.findIndex((fRst) => fRst.id === rstId);

      concepts[conceptIndex].restrictions[rstIndex] = cloneDeep(rst);
    }

    this.setState({ concepts });
  };

  removeRstCopy = (conceptId, rstId) => {
    const { oldRstCopies } = this.state;

    delete oldRstCopies[conceptId][rstId];

    this.setState({ oldRstCopies });
  };

  switchToMacros = (conceptIndex, rstIndex, toMacros) => {
    const concepts = [...this.state.concepts];

    const concept = concepts[conceptIndex];
    const rstCopy = { ...concept.restrictions[rstIndex] };

    const macro = {};

    if (toMacros) {
      rstCopy.macro = macro;
    } else {
      delete rstCopy.macro;
    }

    concepts[conceptIndex].restrictions[rstIndex] = rstCopy;

    this.setState({ concepts });
  };

  addMacroArgument = (index, rstIndex, conceptIndex, argumentName, value) => {
    const concepts = [...this.state.concepts];

    const concept = concepts[conceptIndex];
    const rstCopy = { ...concept.restrictions[rstIndex] };

    editRstMacroArgs(index, argumentName, value, rstCopy.macro);

    concepts[conceptIndex].restrictions[rstIndex] = rstCopy;

    this.setState({ concepts });
  };

  onMacroArgRemove = (index, rstIndex, conceptIndex) => {
    const concepts = [...this.state.concepts];

    const concept = concepts[conceptIndex];
    const rstCopy = { ...concept.restrictions[rstIndex] };

    rstCopy.macro.args.splice(index, 1);
    if (!rstCopy.macro.args.length) delete rstCopy.macro.args;

    concepts[conceptIndex].restrictions[rstIndex] = rstCopy;

    this.setState({ concepts });
  };

  onMacrosRemove = (conceptIndex, rstIndex) => {
    const concepts = [...this.state.concepts];

    const concept = concepts[conceptIndex];
    const rstCopy = { ...concept.restrictions[rstIndex] };

    delete rstCopy.macro;

    concepts[conceptIndex].restrictions[rstIndex] = rstCopy;

    this.toggleDeleteMsgModal(0, 0, 0, "", false, "");

    this.setState({ concepts });
  };

  openBoundsModal = (rst, conceptIndex, rstIndex) => {
    const boundsModal = { ...this.state.boundsModal };

    const isNode = rst.primitive === '_obj' || rst.type === 'OBJECT';

    const bounds = [];

    if (rst.bounds) {
      rst.bounds.forEach((bound) => {
        if (isNode) {
          bounds.push(bound);
        } else {
          if (bound.item) {
            bounds.push({ value: bound.item, alt: bound.alt ? bound.alt : [], oldValue: bound.item, editable: false });
          }
        }
      });
    }

    boundsModal.bounds = bounds;
    boundsModal.isOpened = true;
    boundsModal.rstIndex = rstIndex;
    boundsModal.conceptIndex = conceptIndex;

    if (isNode) {
      boundsModal.type = 'node';

      if (rst.oid) boundsModal.ancestor = rst.oid;
    }

    this.setState({ boundsModal });
  };

  closeBoundsModal = () => {
    const boundsModal = { ...this.state.boundsModal };
    const concepts = [...this.state.concepts];

    const concept = concepts[boundsModal.conceptIndex];
    const rstCopy = { ...concept.restrictions[boundsModal.rstIndex] };

    const isNode = boundsModal.type === 'node';

    rstCopy.bounds = boundsModal.bounds.map(bound => {
      if (isNode) {
        return { node_item: bound.node_item };
      }

      const boundObject = { item: bound.value };
      if (bound.alt) boundObject.alt = bound.alt;

      return boundObject;
    });

    concepts[boundsModal.conceptIndex].restrictions[boundsModal.rstIndex] = rstCopy;

    const emptyBoundsModal = {
      bounds: [],
      boundsNew: '',
      isOpened: false,
    };

    this.setState({ concepts, boundsModal: emptyBoundsModal });
  };

  onBoundFieldChange = (field, index, value, cb) => {
    const { boundsModal } = this.state;

    boundsModal.bounds[index][field] = value;

    this.setState({ boundsModal }, () => {
      if (cb) cb();
    });
  };

  onBoundRemove = (index) => {
    const { boundsModal } = this.state;

    boundsModal.bounds.splice(index, 1);

    this.setState({ boundsModal });
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    const { boundsModal } = this.state;

    boundsModal.bounds = changeArrayElementPosition(boundsModal.bounds, oldIndex, newIndex);

    this.setState({ boundsModal });
  };

  onBoundsNewChange = (value) => {
    const boundsModal = { ...this.state.boundsModal };

    boundsModal.boundsNew = value;

    this.setState({ boundsModal });
  };

  addNewBounds = () => {
    const boundsModal = { ...this.state.boundsModal };

    const strArr = boundsModal.boundsNew.split('\n');

    const bounds = [];

    strArr.forEach((str) => {
      const value = str.trim();

      if (value) bounds.push({ value: str, editable: false });
    });

    boundsModal.bounds = [...boundsModal.bounds, ...bounds];

    boundsModal.boundsNew = '';

    this.setState({ boundsModal });
  };

  addNewNodeBound = (node) => {
    const boundsModal = { ...this.state.boundsModal };
    const nodes = { ...this.state.nodes };

    const boundsCopy = [...boundsModal.bounds];

    boundsCopy.push({ node_item: node.id });

    nodes[node.id] = { ...node };

    boundsModal.bounds = boundsCopy;

    this.setState({ nodes, boundsModal });
  };

  render() {
    const { classes } = this.props;
    const {
      nodes,
      loaded,
      ancestor,
      concepts,
      rstTypes,
      timezones,
      boundsModal,
      deleteModal,
      abstractRstrs,
      functionModal,
      moveSelectedTo,
      usedInterfaces,
      interfacesConceptIds,
     } = this.state;

    return (
      <div className={classes.mainContainer}>
        <ActionConfirmation
          modalData={{
            open: deleteModal.isOpened,
            contentBoldText: `#${deleteModal.rstId} ${deleteModal.rstTitle}?`,
            contentText: deleteModal.contentText,
          }}
          acceptHandler={() => {
            if (deleteModal.contentText === "Delete Restriction") {
              this.deleteRestriction();
            } else {
              this.onMacrosRemove(deleteModal.conceptIndex, deleteModal.rstIndex);
            }
          }}
          onClose={() => this.toggleDeleteMsgModal(0, 0, 0, "", false, "")}
        />

        <FullScreenDialog
          title="Bounds"
          isOpen={boundsModal.isOpened}
          onClose={() => this.closeBoundsModal()}
          paperFullScreenStyles={classes.trplFormPaper}
        >
          <div style={{ display: 'inline-flex', flexDirection: 'column', minWidth: '300px', maxWidth: '600px' }}>
            <BoundsList
              useDragHandle
              nodes={nodes}
              classes={classes}
              boundsModal={boundsModal}
              items={boundsModal.bounds}
              onSortEnd={this.onSortEnd}
              addNewBounds={this.addNewBounds}
              onBoundRemove={this.onBoundRemove}
              addNewNodeBound={this.addNewNodeBound}
              helperClass={classes.draggableItemHelper}
              onBoundsNewChange={this.onBoundsNewChange}
              onBoundFieldChange={this.onBoundFieldChange}
            />
          </div>
        </FullScreenDialog>

        {!loaded ? (
          <div className={classes.justifyCenter}>
            <Loader />
          </div>
        ) : (
          <div>
            <div key={ancestor} className={`${classes.spaceBetween} ${classes.mb20}`}>
              <Node nodeId={ancestor} isLink size="md" node={nodes[ancestor]} nodes={nodes} />
            </div>

            {interfacesConceptIds && interfacesConceptIds.length ? (
              <div className={classes.mb20}>

                <Table style={{ display: 'flex' }}>
                  <TableBody>
                    {interfacesConceptIds.map((cid, conceptIndex) => {
                      if (!abstractRstrs[cid]) return null;
                      const rstrsSize = Object.keys(abstractRstrs[cid]).length;
                      if (!rstrsSize) return null;
                      return (
                        <React.Fragment key={cid}>
                          {Object.values(abstractRstrs[cid]).map((rstAbstr, indexRstAbstr) => {
                            return (
                              <AbstractRestrictionItem
                                key={indexRstAbstr}
                                cid={cid}
                                rstAbstr={rstAbstr}
                                indexRstAbstr={indexRstAbstr}
                                nodes={nodes}
                                rstrsSize={rstrsSize}
                                getRestrictionByImplementedPid={this.getRestrictionByImplementedPid}
                              />
                            );
                          })
                          }
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : null}

            <div>
              {concepts.map((concept, conceptIndex) => {
                const isAncestor = concepts.length - 1 === conceptIndex;
                const rstrsLength = concept.restrictions.length;

                const conceptRestrictionsLength =
                  isAncestor && rstrsLength
                    ? rstrsLength - 1
                    : isAncestor && !rstrsLength
                    ? 0
                    : rstrsLength
                    ? rstrsLength
                    : 0;

                let overridedRstrsCount = 0;
                let deletedRstrsCount = 0;

                let notCountableRstrs = 0;

                concept.restrictions.forEach((rst) => {
                  if (rst.isDeleted || rst.overriding || rst.overrided) notCountableRstrs++;
                });

                return (
                  <RestrictionsAcc
                    key={concept.id}
                    classes={classes}
                    isInheritedConcept={ancestor === concept.id}
                    title={`Restrictions ${nodes[concept.id].title} (${
                      conceptRestrictionsLength - notCountableRstrs
                    }) ${concept.hasOverriders ? '*' : ''}`}
                  >
                    {(!concept.is_abstract) && concept.restrictions.map((rst, rstIndex) => {
                      const enableOverride = !isAncestor && rst.id !== 'OVR' && !rst.overrided && !rst.overridden_by;

                      if (rst.isDeleted) {
                        deletedRstrsCount++;

                        return <DeletedRestriction id={rst.id} key={rstIndex} className={classes.deletedRst} />;
                      }

                      let rstFieldIndex = rstIndex + 1 - overridedRstrsCount - deletedRstrsCount;

                      if (rst.overriding || rst.overrided) {
                        rstFieldIndex = '';
                        overridedRstrsCount++;
                      }

                      if (rst.id === 0) rstFieldIndex = '';

                      //if (!isAncestor && rst.isAbstract) return null;

                      return (
                        <Restriction
                          rst={rst}
                          nodes={nodes}
                          key={rstIndex}
                          rstTypes={rstTypes}
                          rstIndex={rstIndex}
                          concept={concept.id}
                          timezones={timezones}
                          isAncestor={isAncestor}
                          removeRst={this.removeRst}
                          conceptIndex={conceptIndex}
                          rstFieldIndex={rstFieldIndex}
                          functionModal={functionModal}
                          saveRstCopy={this.saveRstCopy}
                          usedInterfaces={usedInterfaces}
                          enableOverride={enableOverride}
                          returnOldRst={this.returnOldRst}
                          removeRstCopy={this.removeRstCopy}
                          switchToMacros={this.switchToMacros}
                          addNodeToState={this.addNodeToState}
                          macro_types={this.props.macro_types}
                          openBoundsModal={this.openBoundsModal}
                          onRstTypeSelect={this.onRstTypeSelect}
                          onRstFieldChange={this.onRstFieldChange}
                          onMacroArgRemove={this.onMacroArgRemove}
                          getRstInterfaces={this.getRstInterfaces}
                          addMacroArgument={this.addMacroArgument}
                          onSaveRstBtnClick={this.onSaveRstBtnClick}
                          interfacesConceptIds={interfacesConceptIds}
                          toggleRstAccordeon={this.toggleRstAccordeon}
                          onOverrideRstClick={this.onOverrideRstClick}
                          onMacroFieldChange={this.onMacroFieldChange}
                          formatters={this.getSelectFormatters(rst.type)}
                          onRstInterfaceSelect={this.onRstInterfaceSelect}
                          toggleDeleteMsgModal={this.toggleDeleteMsgModal}
                          atLeastOneRstOpened={concept.atLeastOneRstOpened}
                          toggleConceptRestrictions={this.toggleConceptRestrictions}
                          onTimezoneDefaultFieldChange={this.onTimezoneDefaultFieldChange}
                        />
                      );
                    })}
                  </RestrictionsAcc>
                );
              })}
            </div>
          </div>
        )}

        {loaded ? (
          <div className={classes.alignCenter} style={{ marginBottom: '100px', padding: '10px', backgroundColor: '#fff' }}>
            <span className={classes.mr10}>move selected rsts to:</span>

            {moveSelectedTo.id ? (
              <Node
                isLink
                size="xs"
                isRemovable
                node={moveSelectedTo}
                className={classes.mr20}
                nodeId={moveSelectedTo.id}
                onRemoveClick={() => {
                  this.setState({ moveSelectedTo: {} });
                }}
              />
            ) : (
              <Search
                conceptsOnly={true}
                onDropdownElementClick={(props) => {
                  this.onMoveToSearchResultClick(props);
                }}
                containerStyles={{
                  height: '24px',
                  background: '#fff',
                  marginRight: '20px',
                }}
              />
            )}

            <Button
              size="small"
              disabled={!moveSelectedTo.id}
              onClick={this.onMoveToBtnClick}
              className={`${classes.btn} ${classes.btnNormal}`}
            >
              OK
            </Button>
          </div>
        ) : null}
      </div>
    );
  }
}

export default connect((state) => (
  {
    setup: state.meta.setup,
    macro_types: state.meta.macro_types,
  }
),{})(withStyles(styles)(RestrictionsPage));
